import React, {useEffect, useRef, useState} from 'react';
import type {PostHog} from 'posthog-js';
import {analyticsAllowed, analyticsConsent, analyticsPath, excludedPath, preferenceKey} from './analytics-policy';

export type AnalyticsConfig = {
  production: boolean; hosts: string; posthogKey?: string; posthogHost?: string;
  umamiHost?: string; umamiWebsiteId?: string;
};

declare global {
  interface Window {
    umami?: {track: (payload: Record<string, unknown>) => void};
  }
}

function storedConsent(): boolean | null {
  try { return analyticsConsent(localStorage.getItem(preferenceKey)); } catch { return null; }
}

const css = `
.fa-cookie-panel{position:fixed;inset:auto 16px 16px auto;z-index:10000;max-width:420px;width:calc(100% - 32px);box-sizing:border-box;background:#f8f8f2;color:#17231d;border:1px solid #738077;box-shadow:0 8px 32px #0003;padding:22px;font:14px/1.6 system-ui,sans-serif;border-radius:12px}
.fa-cookie-panel h2{font:600 19px/1.3 system-ui,sans-serif;margin:0 0 10px}.fa-cookie-panel p{margin:0 0 16px}.fa-cookie-actions{display:flex;gap:10px;flex-wrap:wrap}.fa-cookie-panel button,.fa-cookie-settings{font:600 14px/1.4 system-ui,sans-serif;min-height:44px;padding:10px 16px;border:1px solid #516057;border-radius:6px;background:#f8f8f2;color:#17231d;cursor:pointer}.fa-cookie-panel .fa-cookie-accept{background:#17231d;color:#fff}.fa-cookie-footer{position:static;display:flex;justify-content:center;padding:8px 16px max(8px,env(safe-area-inset-bottom));background:#f8f8f2;border-top:1px solid #c4ccc5}.fa-cookie-settings{position:static;font-size:12px;padding:6px 10px;border:0;text-decoration:underline;text-underline-offset:3px}.fa-cookie-panel button:focus-visible,.fa-cookie-settings:focus-visible{outline:3px solid #23623b;outline-offset:3px}
`;

/** Shared by the Next.js layout and the Phoenix root layout, outside LiveView patches. */
export function Analytics({config}: {config: AnalyticsConfig}) {
  const [choice, setChoice] = useState<boolean | null>(null);
  const [visible, setVisible] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const lastUmami = useRef('');
  const lastPosthog = useRef('');

  useEffect(() => {
    const permitted = () => analyticsAllowed(config.production, location.hostname, config.hosts, location.pathname)
      && Boolean(config.posthogKey || config.umamiWebsiteId);
    const refresh = () => { setAllowed(permitted()); setChoice(storedConsent()); };
    refresh();
    setVisible(storedConsent() === null);
    window.addEventListener('storage', refresh);
    window.addEventListener('phx:page-loading-stop', refresh);
    window.addEventListener('popstate', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('phx:page-loading-stop', refresh);
      window.removeEventListener('popstate', refresh);
    };
  }, [config]);

  useEffect(() => {
    if (!allowed || navigator.doNotTrack === '1') return;
    let active = true;
    let posthog: PostHog | undefined;
    const permitted = () => active && !excludedPath(location.pathname);
    const pageview = () => {
      if (!permitted()) {
        if (excludedPath(location.pathname)) { setAllowed(false); posthog?.opt_out_capturing(); }
        return;
      }
      const path = analyticsPath(location.pathname);
      if (posthog && lastPosthog.current !== path) {
        lastPosthog.current = path;
        posthog.capture('$pageview', {$current_url: location.origin + path, $pathname: path});
      }
      if (config.umamiWebsiteId && window.umami && lastUmami.current !== path) {
        lastUmami.current = path;
        window.umami.track({website: config.umamiWebsiteId, hostname: location.hostname,
          url: path, title: path, referrer: safeReferrer()});
      }
    };
    const safeReferrer = () => { try { return new URL(document.referrer).origin; } catch { return ''; } };
    if (config.posthogKey) {
      void import('posthog-js').then(({default: sdk}) => {
        if (!permitted()) return;
        posthog = sdk;
        const settings: Parameters<PostHog['init']>[1] = {
          api_host: config.posthogHost || 'https://eu.i.posthog.com',
          ui_host: 'https://eu.posthog.com',
          capture_pageview: false, capture_pageleave: false, autocapture: false,
          capture_exceptions: false, capture_heatmaps: false, capture_performance: false,
          capture_dead_clicks: false, rageclick: false, disable_session_recording: true,
          disable_surveys: true, advanced_disable_flags: true, person_profiles: 'never',
          respect_dnt: true, persistence: 'localStorage+cookie', cross_subdomain_cookie: false,
          cookieless_mode: 'on_reject', opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true,
          before_send: event => {
            if (!permitted() || !event || event.event !== '$pageview') return null;
            const path = analyticsPath(location.pathname);
            // Keep useful browser properties. Remove raw URLs, titles, and initial referrers.
            for (const key of Object.keys(event.properties)) {
              if (/url|pathname|referr|title|initial/i.test(key)) delete event.properties[key];
            }
            delete event.properties.$set;
            delete event.properties.$set_once;
            event.properties.$current_url = location.origin + path;
            event.properties.$pathname = path;
            event.properties.$referrer = safeReferrer();
            return event;
          }
        };
        if (!sdk.__loaded) sdk.init(config.posthogKey!, settings);
        else sdk.set_config(settings);
        if (storedConsent() === true) sdk.opt_in_capturing({captureEventName: false});
        else sdk.opt_out_capturing();
        pageview();
      }).catch(() => { /* Analytics must not interrupt the game. */ });
    }
    let script: HTMLScriptElement | undefined;
    if (config.umamiWebsiteId && !window.umami) {
      script = document.createElement('script');
      script.src = (config.umamiHost || 'https://analytics.interactivecats.com').replace(/\/$/, '') + '/script.js';
      script.dataset.websiteId = config.umamiWebsiteId;
      script.dataset.autoTrack = 'false';
      script.dataset.doNotTrack = 'true';
      script.onload = pageview;
      script.async = true;
      document.head.appendChild(script);
    }
    const originalPush = history.pushState;
    const originalReplace = history.replaceState;
    const push: History['pushState'] = function(this: History, ...args) { originalPush.apply(this, args); queueMicrotask(pageview); };
    const replace: History['replaceState'] = function(this: History, ...args) { originalReplace.apply(this, args); queueMicrotask(pageview); };
    history.pushState = push;
    history.replaceState = replace;
    window.addEventListener('popstate', pageview);
    window.addEventListener('phx:page-loading-stop', pageview);
    pageview();
    return () => {
      active = false;
      posthog?.opt_out_capturing();
      script?.remove();
      if (history.pushState === push) history.pushState = originalPush;
      if (history.replaceState === replace) history.replaceState = originalReplace;
      window.removeEventListener('popstate', pageview);
      window.removeEventListener('phx:page-loading-stop', pageview);
    };
  }, [allowed, choice, config]);

  function save(analytics: boolean) {
    try {
      const existing = JSON.parse(localStorage.getItem(preferenceKey) || '{}');
      localStorage.setItem(preferenceKey, JSON.stringify({...existing, necessary: true, analytics}));
      localStorage.setItem('fraud-attacks-cookie-consent', 'true');
    } catch { /* A blocked storage API must not block play. */ }
    setChoice(analytics);
    setVisible(false);
  }

  if (!allowed) return null;
  return <><style>{css}</style>{visible ?
    <section className="fa-cookie-panel" role="dialog" aria-labelledby="fa-cookie-title" aria-describedby="fa-cookie-description">
      <h2 id="fa-cookie-title">Your cookie choice</h2>
      <p id="fa-cookie-description">We use essential cookies to keep you signed in and run the game. PostHog and Umami measure page visits without analytics cookies. If you accept, PostHog also uses cookies to connect your visits. Your choice does not change your email preferences.</p>
      <div className="fa-cookie-actions"><button onClick={() => save(false)}>Reject analytics cookies</button><button className="fa-cookie-accept" onClick={() => save(true)}>Accept analytics cookies</button></div>
    </section> : <footer className="fa-cookie-footer" aria-label="Privacy settings"><button className="fa-cookie-settings" onClick={() => setVisible(true)}>Cookie settings</button></footer>}</>;
}
